import {
	forwardRef,
	memo,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import styled, { css } from 'styled-components';
import { Button, ConfigProvider, Spin } from 'antd';
import { LuMaximize, LuMaximize2, LuMinimize, LuPause, LuPlay } from 'react-icons/lu';
import { frameAt, lineCrossings, shownFrom } from './tracksModel';
import {
	DRAG_PX,
	PLAIN,
	panBy,
	viewTransform,
	wheelFactor,
	zoomAt,
	zoomMax,
	zoomedRect,
} from './view';
import { frameAt as stillAt } from '@utils/video/tsPreview';
import { keepLineInside } from '@utils/video/helpers';
import { cornerPath } from './detectionStyle';
import { labelPlace } from './pendingAdds';
import { pickTrack } from './pickTrack';

/**
 * The analysed clip with the detections drawn over it on a canvas (nothing is burnt into the video): a small,
 * see-through dot per animal, a short trail, a pulse when an animal is first counted (with the path it took in the
 * second before, fading), and the counting line, which can be dragged when `lineEditable` and flashes +1 where an
 * animal is counted across it. Paints once per video frame (requestVideoFrameCallback where available).
 * `pip` is a second view (the flight map) in the corner of the picture; `swapped` puts it on the stage and the
 * video in the corner, and `onSwap` shows the button that switches the two. Both stay mounted either way.
 */

const GREEN = '32, 172, 107';
const LIGHT_GREEN = '144, 215, 173';
const AMBER = '232, 170, 60';
const RED = '235, 87, 87';
const TRAIL_SECONDS = 1;
const PULSE_FRAMES = 8;
// how long the path an animal took before it was counted shows when it pops up, and a +1 where it crossed the line
const ENTRY_SECONDS = 0.9;
const CROSS_SECONDS = 1;
const HANDLE_PX = 14;
// why Play is off (canPlay false): the button's tooltip and its name for a screen reader
const WAIT_TO_PLAY = 'The video plays when the analysis is done';
// the least content width of the control bar that shows the key hints on the transport row. Measured 26 Oct 2026:
// Play, the seek bar at its least width, the time, the speed, the zoom chip, Full screen, the hints and the gaps
// between them add up to about 820 px; narrower, the hints would wrap, and a taller bar shrinks the picture on a
// page that fits the picture to the height the bar leaves. A container query measures the content box.
const KEYS_MIN_BAR_PX = 840;

const Wrap = styled.div`
	position: relative;
	width: 100%;
	background: #050605;
	border: 1px solid rgba(70, 74, 70, 0.75);
	border-radius: 16px;
	overflow: hidden;
	aspect-ratio: ${({ $ratio }) => $ratio || '16 / 9'};
	max-height: 100%;
`;

// the player's frame alone: where the video will be while the results load
export const PlayerFrame = Wrap;

// the picture with its control bar: what goes full screen, as a column that gives the picture all the height the
// bar leaves (the frame's aspect ratio would otherwise push the bar off the screen)
const Player = styled.div`
	&:fullscreen {
		display: flex;
		flex-direction: column;
		padding: ${({ theme }) => theme.token.paddingSM}px;
		background: ${({ theme }) => theme.token.colorBgBase};
	}
	&:fullscreen > ${Wrap} {
		flex: 1;
		min-height: 0;
		aspect-ratio: auto;
		max-height: none;
	}
`;

// the middle of the frame, for a spinner while the results or the video load; clicks go through to the picture,
// and it sits where it is rendered (no z-index), under anything laid over the picture
export const FrameCentre = styled.div`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	pointer-events: none;
`;

// what the frame says when the video failed to load, with the button to try again; only the note itself takes
// clicks, the rest of the picture keeps its tools
const FailedNote = styled.div`
	pointer-events: auto;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${({ theme }) => theme.token.marginXS}px;
	padding: ${({ theme }) => `${theme.token.paddingSM}px ${theme.token.padding}px`};
	border-radius: ${({ theme }) => theme.token.borderRadiusLG}px;
	background: ${({ theme }) => theme.token.panelGlassBg};
	color: ${({ theme }) => theme.token.colorText};
	font-size: ${({ theme }) => theme.token.fontSizeSM}px;
`;

// the small view in a corner of the stage: the map bottom-right over the video, or the video bottom-left over
// the map, where the map keeps its zoom and scale controls in the right corner
const cornerCss = css`
	top: auto;
	bottom: ${({ theme }) => theme.token.marginSM}px;
	width: 38%;
	border-radius: ${({ theme }) => theme.token.borderRadiusLG}px;
	overflow: hidden;
	box-shadow: ${({ theme }) => theme.token.boxShadowSecondary};
	z-index: 3;
`;

const Picture = styled.div`
	position: absolute;
	inset: 0;
	${({ $pip, $ratio, theme }) =>
		$pip &&
		css`
			${cornerCss};
			right: auto;
			left: ${theme.token.marginSM}px;
			aspect-ratio: ${$ratio || '16 / 9'};
			border: ${theme.token.lineWidth}px ${theme.token.lineType} ${theme.token.colorBorder};
		`}
`;

// The counting line is drawn on the canvas, so a tour has no element to point at: passed as a child while a line is
// shown, this covers the picture and lets every click through to the canvas.
export const PictureTarget = styled.div`
	position: absolute;
	inset: 0;
	pointer-events: none;
`;

const PipSlot = styled.div`
	position: absolute;
	inset: 0;
	z-index: 1;
	${({ $pip, $small, theme }) =>
		$pip &&
		css`
			${cornerCss};
			left: auto;
			right: ${theme.token.marginSM}px;
			aspect-ratio: 4 / 3;
			${$small && 'width: 24%;'}
		`}
`;

const SwapButton = styled(Button)`
	position: absolute;
	top: ${({ theme }) => theme.token.marginXS}px;
	right: ${({ theme }) => theme.token.marginXS}px;
	z-index: 4;
`;

const Corner = styled.div`
	position: absolute;
	top: ${({ theme }) => theme.token.marginXS}px;
	right: ${({ theme }) => theme.token.marginXS}px;
	z-index: 4;
`;

// the zoom (view.js) is a transform from the frame's top-left corner, so the canvas mapping can mirror it
const Video = styled.video`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: contain;
	display: block;
	transform-origin: 0 0;
`;

// a video the browser cannot play (.ts) shows still frames from it instead
const Still = styled.img`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: contain;
	display: block;
	transform-origin: 0 0;
`;

const Canvas = styled.canvas`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
	cursor: ${({ $cursor }) => $cursor || 'default'};
	touch-action: none;
`;

const Controls = styled.div`
	container-type: inline-size;
	display: flex;
	flex-wrap: wrap;
	row-gap: ${({ theme }) => theme.token.marginXS}px;
	align-items: center;
	gap: 0.875rem;
	margin-top: 0.75rem;
	min-height: 52px;
	padding: 0.4rem 1.1rem;
	background: rgba(28, 30, 28, 0.86);
	border: 1px solid rgba(70, 74, 70, 0.75);
	border-radius: 14px;
	color: #b4b9b4;
	font-variant-numeric: tabular-nums;
	font-size: 0.8rem;
`;

const PlayButton = styled.button`
	display: grid;
	place-items: center;
	flex: none;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	border: 0;
	background: ${({ theme }) => theme.colors.darkGreen};
	color: #fff;
	cursor: pointer;
	&:hover {
		background: ${({ theme }) => theme.colors.green};
	}
	&:disabled {
		background: ${({ theme }) => theme.token.colorBgContainerDisabled};
		color: ${({ theme }) => theme.token.colorTextDisabled};
		cursor: not-allowed;
	}
`;

const Scrub = styled.input`
	width: 100%;
	accent-color: ${({ theme }) => theme.colors.green};
	min-width: 0;
`;

// the seek bar with a tick above it at each frame someone corrected (green added, red removed)
const ScrubWrap = styled.div`
	position: relative;
	flex: 1;
	min-width: ${({ theme }) => theme.token.controlHeight * 4}px;
	display: flex;
	align-items: center;
`;

// the player's toggles and actions (chips), on their own row under the seek bar so the bar keeps its width
const ChipRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${({ theme }) => theme.token.marginXS}px;
	flex-basis: 100%;
`;

const EditMark = styled.span`
	position: absolute;
	top: ${({ theme }) => -theme.token.sizeXXS}px;
	width: ${({ theme }) => theme.token.lineWidthBold}px;
	height: ${({ theme }) => theme.token.sizeXS}px;
	border-radius: ${({ theme }) => theme.token.borderRadiusXS}px;
	background: ${({ theme, $kind }) =>
		$kind === 'remove' ? theme.token.colorError : theme.token.colorSuccess};
	pointer-events: none;
`;

const Speed = styled.select`
	background: ${({ theme }) => theme.colors.backgroundInput};
	color: ${({ theme }) => theme.colors.textColor};
	border: 1px solid ${({ theme }) => theme.colors.borderInput};
	border-radius: 6px;
	padding: 2px 4px;
`;

// the zoom chip reads the same width at every zoom (one decimal, figures of one width), so zooming never wraps
// the transport row: a taller bar would shrink the picture, and a picture that changed size drops the zoom
const ZoomChip = styled(Button)`
	font-variant-numeric: tabular-nums;
`;

// the keys the page answers while the video is on it, said once in the bar so people find them
const Keys = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${({ theme }) => theme.token.marginXXS}px;
	color: ${({ theme }) => theme.token.colorTextTertiary};
	font-size: ${({ theme }) => theme.token.fontSizeXS}px;
	white-space: nowrap;
	/* a bar too narrow for the hints on the transport row drops them rather than wrap (KEYS_MIN_BAR_PX says why);
	   Play still says Space in its title */
	@container (max-width: ${KEYS_MIN_BAR_PX - 0.02}px) {
		display: none;
	}
	kbd {
		/* Bootstrap fills kbd; the caps are outlined like the correcting toolbar's */
		background: transparent;
		font-family: inherit;
		font-size: ${({ theme }) => theme.token.fontSizeXXS}px;
		line-height: 1;
		color: ${({ theme }) => theme.token.colorTextSecondary};
		border: ${({ theme }) =>
			`${theme.token.lineWidth}px ${theme.token.lineType} ${theme.token.colorBorder}`};
		border-radius: ${({ theme }) => theme.token.borderRadiusXS}px;
		padding: ${({ theme }) => `${theme.token.lineWidthBold}px ${theme.token.paddingXXS}px`};
	}
`;

const fmt = s => {
	if (!Number.isFinite(s)) return '0:00.0';
	const m = Math.floor(s / 60);
	return `${m}:${(s % 60).toFixed(1).padStart(4, '0')}`;
};

/** Where the video picture sits inside the element (object-fit: contain letterboxing), in CSS pixels. */
const contentRect = (el, vw, vh) => {
	const w = el.clientWidth;
	const h = el.clientHeight;
	if (!vw || !vh) return { x: 0, y: 0, w, h };
	const s = Math.min(w / vw, h / vh);
	return { x: (w - vw * s) / 2, y: (h - vh * s) / 2, w: vw * s, h: vh * s };
};

// checking the count's magnifier: its share of the picture's shorter side, how much around the track it shows
// (times the box's longer side), and the least it shows in video pixels (a tiny box is not blown up to mush)
const MAGNIFY_SHARE = 0.42;
const MAGNIFY_CONTEXT = 2.5;
const MAGNIFY_MIN_SRC = 72;

const OverlayPlayer = forwardRef(
	(
		{
			src,
			index,
			line,
			lineEditable = false,
			onLineChange,
			onFrame,
			aspect,
			showDetections = true,
			showTrails = true,
			// corrections: called with { id, frame, x, y } (x, y in CSS pixels of the picture) when a dot is clicked;
			// selectedIds get a white ring, tracks a person added an amber one
			onPick = null,
			selectedIds = null,
			// checking the count: the track asked about (an id), everything else in the picture dimmed and it ringed
			spotlight = null,
			// the assistant's proposals, dashed: { negative: track id } or { frame, box: [x1, y1, x2, y2] } (an add)
			proposals = null,
			// correcting: 'click' (onAddPoint(frame, [x, y])) or 'box' (drag, onAddBox(frame, [x1, y1, x2, y2])), in
			// source pixels; pendingAdds ({ frame, box, point, status, label }, see pendingAdds.js) are objects added
			// and not followed yet, marked at the point when there is one, else at the box
			tool = null,
			onAddPoint = null,
			onAddBox = null,
			pendingAdds = null,
			// detections drawn as 'dot' or 'box'
			marker = 'dot',
			// several kinds: { label: { rgb } } a color each (kinds.kindColors); else everything in `color`
			kindColors = null,
			// the corner view smaller (a bigger video: the map takes less of it)
			pipSmall = false,
			// a control in the video's top-right corner, while the video is the large view
			corner = null,
			// told whether the player fills the screen: the page's popups (a tour) cannot show over it
			onFullscreenChange = null,
			// the viewer's color for a one-kind video ("r, g, b", detectionStyle), green by default
			color = null,
			// the video starts playing when it is opened (muted, so browsers allow it)
			autoPlay = true,
			// 'anonymous': the video loads with CORS, so its frames can be read back (the PDF report reads them)
			crossOrigin = undefined,
			// false: Play is off and says why, and a click on the picture does not play the video (autoPlay is
			// separate); the video still pauses, seeks and steps
			canPlay = true,
			// ticks on the seek bar: [{ sec, kind: 'add' | 'remove' }]
			editMarks = null,
			// the page answers Space and the arrows (VideoProjectView): Play says so and the bar lists the keys
			shortcuts = false,
			extraControls = null,
			// asked for a fresh source after a load error (a re-signed link); without it the same source reloads
			onReload = null,
			pip = null,
			swapped = false,
			onSwap,
			stills = null,
			stillsDuration = 0,
			children,
		},
		ref
	) => {
		const rootRef = useRef(null);
		const wrapRef = useRef(null);
		const videoRef = useRef(null);
		const stillRef = useRef(null);
		const canvasRef = useRef(null);
		const controlsRef = useRef(null);
		// the zoom (view.js): kept in a ref and applied by hand, since the wheel and a drag change it many times a
		// second; `zoom` is the rounded factor the bar shows (and whether the canvas takes drags), `panning` the cursor
		const viewRef = useRef(PLAIN);
		const panRef = useRef(null);
		const sizeRef = useRef(null);
		const [zoom, setZoom] = useState(1);
		const [panning, setPanning] = useState(false);
		const zoomed = zoom > 1;
		// the player (picture and bar) fills the screen; Esc or the button leave it. Only where the browser allows
		// an element to go full screen (not on an iPhone)
		const canFullscreen = !!document.fullscreenEnabled;
		const [fullscreen, setFullscreen] = useState(false);
		useEffect(() => {
			const onChange = () =>
				setFullscreen(!!rootRef.current && document.fullscreenElement === rootRef.current);
			document.addEventListener('fullscreenchange', onChange);
			return () => document.removeEventListener('fullscreenchange', onChange);
		}, []);
		useEffect(() => {
			onFullscreenChange?.(fullscreen);
		}, [fullscreen, onFullscreenChange]);
		const toggleFullscreen = () => {
			// only this player's own full screen is left; refused requests (no user gesture, an iframe without
			// the permission) have nothing to show
			if (document.fullscreenElement === rootRef.current)
				document.exitFullscreen?.()?.catch?.(() => {});
			else rootRef.current?.requestFullscreen?.()?.catch?.(() => {});
		};
		// antd pop-ups (the Export menu, Details) go to the body, which full screen hides, so while the player is
		// full screen they go inside it. A stable function: a new one each render would remake antd's config
		// context on every time update
		const toRoot = useCallback(() => rootRef.current, []);
		const popupContainer = fullscreen ? toRoot : undefined;
		const frameRef = useRef(0);
		const dragRef = useRef(null);
		const boxDragRef = useRef(null);
		const [playing, setPlaying] = useState(false);
		const [time, setTime] = useState(0);
		const [duration, setDuration] = useState(0);
		const [cursor, setCursor] = useState('default');
		const [stillTime, setStillTime] = useState(0);
		const still = stills ? stillAt(stills, stillTime) : null;
		// whether the video has a frame to show: a 4K file from S3 can take many seconds, and a black frame says
		// nothing, so the picture shows a spinner until it can play and again while it waits for data. A new
		// source starts loading again; adjusted during render so no frame shows the old state for the new file.
		const [buffering, setBuffering] = useState(!!src);
		// the browser could not load or decode the video: said in the frame, with a way to try again. The note
		// stays (and its button spins) until the retry settles, so focus is not dropped and a second failure
		// lands where the user is looking.
		const [failed, setFailed] = useState(false);
		const [retrying, setRetrying] = useState(false);
		const [loadedSrc, setLoadedSrc] = useState(src);
		if (loadedSrc !== src) {
			setLoadedSrc(src);
			setBuffering(!!src);
			setFailed(false);
			setRetrying(false);
		}
		// onReload asks the owner for a fresh link (a signed URL that expired reloads as the same expired URL);
		// without it the element loads the same source again
		const reload = () => {
			setRetrying(true);
			setBuffering(true);
			if (onReload) onReload();
			else videoRef.current?.load();
		};
		const settled = () => {
			setBuffering(false);
			setFailed(false);
			setRetrying(false);
		};

		useImperativeHandle(ref, () => ({
			seekTo: sec => {
				if (stills) setStillTime(Math.max(0, sec));
				else if (videoRef.current) videoRef.current.currentTime = Math.max(0, sec);
			},
			video: () => videoRef.current,
			// the time shown: the still frame's for a video that only has stills
			now: () => (stills ? (still?.t ?? 0) : videoRef.current?.currentTime || 0),
			// the control bar under the picture, for a parent that fits the picture into a fixed height
			controls: () => controlsRef.current,
		}));

		// where the plain (not zoomed) picture sits in the frame, in CSS pixels; the frame itself before the video
		// has a size
		const baseRect = () => {
			const wrap = wrapRef.current;
			if (!wrap) return null;
			const v = videoRef.current;
			return contentRect(
				wrap,
				v?.videoWidth || index?.width,
				v?.videoHeight || index?.height
			);
		};

		// where and when each crossing of the counting line counts, for its +1
		const crossingMarks = useMemo(
			() => (index && line ? lineCrossings(index, line).marks : null),
			[index, line]
		);

		// source pixels (tracks.json) → canvas device pixels, the zoom included; `r` is the zoomed picture in CSS
		// pixels, for the cards placed next to a point
		const mapping = useCallback(() => {
			const wrap = wrapRef.current;
			const v = videoRef.current;
			if (!wrap || !v || !index) return null;
			const r = zoomedRect(
				contentRect(wrap, v.videoWidth || index.width, v.videoHeight || index.height),
				viewRef.current
			);
			const dpr = window.devicePixelRatio || 1;
			const s = (r.w / index.width) * dpr;
			return { s, ox: r.x * dpr, oy: r.y * dpr, dpr, r };
		}, [index]);

		const paint = useCallback(
			frame => {
				const canvas = canvasRef.current;
				const wrap = wrapRef.current;
				if (!canvas || !wrap) return;
				const dpr = window.devicePixelRatio || 1;
				const W = Math.round(wrap.clientWidth * dpr);
				const H = Math.round(wrap.clientHeight * dpr);
				if (canvas.width !== W || canvas.height !== H) {
					canvas.width = W;
					canvas.height = H;
				}
				const ctx = canvas.getContext('2d');
				ctx.clearRect(0, 0, W, H);
				const m = mapping();
				if (!m || !index) return;
				const drag = boxDragRef.current;
				if (drag) {
					ctx.save();
					ctx.setLineDash([6 * dpr, 4 * dpr]);
					ctx.lineWidth = 2 * dpr;
					ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
					const [ax, ay] = [m.ox + drag.from[0] * m.s, m.oy + drag.from[1] * m.s];
					const [bx, by] = [m.ox + drag.to[0] * m.s, m.oy + drag.to[1] * m.s];
					ctx.strokeRect(
						Math.min(ax, bx),
						Math.min(ay, by),
						Math.abs(bx - ax),
						Math.abs(by - ay)
					);
					ctx.restore();
				}
				const X = x => m.ox + x * m.s;
				const Y = y => m.oy + y * m.s;
				const { perFrame, trackOf, pointOf, tracks, fps } = index;
				const f = Math.min(index.nFrames - 1, Math.max(0, frame));
				const lo = perFrame[f];
				const hi = perFrame[f + 1];
				const trail = Math.max(2, Math.round(TRAIL_SECONDS * fps));
				const several = Object.keys(kindColors || {}).length > 1;
				const colorOf = tr => (several && kindColors[tr.label]?.rgb) || color || GREEN;

				if (showDetections) {
					if (showTrails) {
						// trails: the last TRAIL_SECONDS of each visible animal, older parts fainter, cut at jumps over
						// half a body length (a tracker hop between neighbors is not a path)
						const BANDS = 4;
						const pathsOf = new Map();
						for (let i = lo; i < hi; i += 1) {
							const tr = tracks[trackOf[i]];
							const j = pointOf[i];
							const rgb = colorOf(tr);
							if (!pathsOf.has(rgb))
								pathsOf.set(
									rgb,
									Array.from({ length: BANDS }, () => new Path2D())
								);
							const paths = pathsOf.get(rgb);
							const body = 0.5 * Math.sqrt(Math.max(1, tr.w[j] * tr.h[j]));
							const from = shownFrom(tr);
							for (let k = j; k > 0; k -= 1) {
								const age = f - tr.t[k - 1];
								if (age > trail || tr.t[k - 1] < from) break;
								if (Math.hypot(tr.x[k] - tr.x[k - 1], tr.y[k] - tr.y[k - 1]) > body)
									break;
								const path =
									paths[Math.min(BANDS - 1, Math.floor((age / trail) * BANDS))];
								path.moveTo(X(tr.x[k]), Y(tr.y[k]));
								path.lineTo(X(tr.x[k - 1]), Y(tr.y[k - 1]));
							}
						}
						ctx.lineWidth = 2.5 * dpr;
						ctx.lineCap = 'round';
						pathsOf.forEach((paths, rgb) =>
							paths.forEach((path, band) => {
								ctx.strokeStyle = `rgba(${rgb}, ${0.8 * (1 - band / BANDS)})`;
								ctx.stroke(path);
							})
						);

						// when an animal pops up (is counted), the path it took in the second before, dotted and fading
						const entry = Math.max(2, Math.round(ENTRY_SECONDS * fps));
						ctx.save();
						ctx.lineCap = 'round';
						ctx.setLineDash([0.5 * dpr, 7 * dpr]);
						for (let i = lo; i < hi; i += 1) {
							const tr = tracks[trackOf[i]];
							const from = shownFrom(tr);
							const age = f - from;
							if (age < 0 || age >= entry || !(tr.t[0] < from)) continue;
							const j = pointOf[i];
							const body = 0.5 * Math.sqrt(Math.max(1, tr.w[j] * tr.h[j]));
							const path = new Path2D();
							path.moveTo(X(tr.x[0]), Y(tr.y[0]));
							for (let k = 1; k < tr.t.length && tr.t[k - 1] < from; k += 1) {
								const jump =
									Math.hypot(tr.x[k] - tr.x[k - 1], tr.y[k] - tr.y[k - 1]) > body;
								if (jump) path.moveTo(X(tr.x[k]), Y(tr.y[k]));
								else path.lineTo(X(tr.x[k]), Y(tr.y[k]));
							}
							const fade = 1 - age / entry;
							ctx.strokeStyle = `rgba(0, 0, 0, ${0.45 * fade})`;
							ctx.lineWidth = 5 * dpr;
							ctx.stroke(path);
							ctx.strokeStyle = `rgba(${LIGHT_GREEN}, ${fade})`;
							ctx.lineWidth = 3 * dpr;
							ctx.stroke(path);
						}
						ctx.restore();
					}

					const picked = selectedIds?.length ? new Set(selectedIds) : null;
					if (marker === 'box') {
						// boxes: the object's corners over a dark edge, so they read on snow and sky alike; light
						// green for what a person added, white when picked
						ctx.lineCap = 'square';
						for (let i = lo; i < hi; i += 1) {
							const tr = tracks[trackOf[i]];
							const j = pointOf[i];
							const sel = picked?.has(tr.id);
							const path = cornerPath(
								new Path2D(),
								X(tr.x[j] - tr.w[j] / 2),
								Y(tr.y[j] - tr.h[j] / 2),
								tr.w[j] * m.s,
								tr.h[j] * m.s,
								4 * dpr,
								18 * dpr
							);
							const width = (sel ? 2.5 : 2) * dpr;
							ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)';
							ctx.lineWidth = width + 2 * dpr;
							ctx.stroke(path);
							ctx.strokeStyle = sel
								? 'rgba(255, 255, 255, 0.95)'
								: tr.added
									? `rgba(${LIGHT_GREEN}, 0.95)`
									: `rgba(${colorOf(tr)}, 0.95)`;
							ctx.lineWidth = width;
							ctx.stroke(path);
						}
					} else {
						// dots: small and see-through so the animal stays visible
						const r = Math.max(3, 3.5 * dpr);
						const dotsOf = new Map();
						for (let i = lo; i < hi; i += 1) {
							const tr = tracks[trackOf[i]];
							const j = pointOf[i];
							const rgb = colorOf(tr);
							if (!dotsOf.has(rgb)) dotsOf.set(rgb, new Path2D());
							const cx = X(tr.x[j]);
							const cy = Y(tr.y[j]);
							dotsOf.get(rgb).moveTo(cx + r, cy);
							dotsOf.get(rgb).arc(cx, cy, r, 0, Math.PI * 2);
						}
						ctx.strokeStyle = 'rgba(15, 15, 15, 0.6)';
						ctx.lineWidth = 1 * dpr;
						dotsOf.forEach((path, rgb) => {
							ctx.fillStyle = `rgba(${rgb}, 0.65)`;
							ctx.fill(path);
							ctx.stroke(path);
						});

						// rings: what a person added (light green) and what is picked for a correction (white)
						for (let i = lo; i < hi; i += 1) {
							const tr = tracks[trackOf[i]];
							const sel = picked?.has(tr.id);
							if (!tr.added && !sel) continue;
							const j = pointOf[i];
							ctx.strokeStyle = sel
								? 'rgba(255, 255, 255, 0.95)'
								: `rgba(${LIGHT_GREEN}, 0.95)`;
							ctx.lineWidth = (sel ? 2.5 : 2) * dpr;
							ctx.beginPath();
							ctx.arc(X(tr.x[j]), Y(tr.y[j]), 11 * dpr, 0, Math.PI * 2);
							ctx.stroke();
						}
					}

					// the assistant's proposals: a red dashed ring on a dot it would take out, a light-green dashed box where
					// it saw one without a dot
					if (proposals?.length) {
						ctx.save();
						ctx.setLineDash([5 * dpr, 4 * dpr]);
						ctx.lineWidth = 2 * dpr;
						const out = new Map(
							proposals.filter(q => q.negative != null).map(q => [q.negative, q.n])
						);
						// the proposal's number beside its mark, white with a dark edge
						const number = (n, x, y) => {
							if (n == null) return;
							ctx.save();
							ctx.setLineDash([]);
							ctx.font = `800 ${14 * dpr}px sans-serif`;
							ctx.lineJoin = 'round';
							ctx.lineWidth = 4 * dpr;
							ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
							ctx.strokeText(String(n), x, y);
							ctx.fillStyle = 'rgba(255, 255, 255, 1)';
							ctx.fillText(String(n), x, y);
							ctx.restore();
						};
						ctx.strokeStyle = `rgba(${RED}, 0.95)`;
						for (let i = lo; i < hi; i += 1) {
							const tr = tracks[trackOf[i]];
							if (!out.has(tr.id)) continue;
							const j = pointOf[i];
							ctx.beginPath();
							ctx.arc(X(tr.x[j]), Y(tr.y[j]), 12 * dpr, 0, Math.PI * 2);
							ctx.stroke();
							number(out.get(tr.id), X(tr.x[j]) + 15 * dpr, Y(tr.y[j]) - 10 * dpr);
						}
						ctx.strokeStyle = `rgba(${LIGHT_GREEN}, 0.95)`;
						ctx.setLineDash([10 * dpr, 6 * dpr]);
						ctx.lineWidth = 3 * dpr;
						const near = Math.max(3, Math.round(fps / 3));
						proposals.forEach(q => {
							if (!q.box || Math.abs(q.frame - f) > near) return;
							const [x1, y1, x2, y2] = q.box;
							ctx.strokeRect(X(x1), Y(y1), X(x2) - X(x1), Y(y2) - Y(y1));
							number(q.n, X(x2) + 4 * dpr, Y(y1) + 4 * dpr);
						});
						ctx.restore();
					}

					// objects people added that are being followed (light-green dashed, breathing) or could not be followed
					// (amber)
					if (pendingAdds?.length) {
						const nearAdd = Math.max(3, Math.round(fps / 3));
						const breath = 0.45 + 0.5 * (0.5 + 0.5 * Math.sin(performance.now() / 190));
						ctx.save();
						ctx.lineWidth = 2 * dpr;
						ctx.font = `600 ${11 * dpr}px sans-serif`;
						ctx.lineJoin = 'round';
						pendingAdds.forEach(q => {
							if (Math.abs(q.frame - f) > nearAdd) return;
							const following = q.status === 'following';
							const asking = q.status === 'confirm';
							const rgb = asking ? '255, 255, 255' : following ? LIGHT_GREEN : AMBER;
							ctx.setLineDash(following || asking ? [6 * dpr, 4 * dpr] : []);
							ctx.strokeStyle = `rgba(${rgb}, ${following ? breath : 0.95})`;
							ctx.fillStyle = `rgba(${rgb}, 0.1)`;
							// the mark, and its edges for the label beside it
							let mark;
							if (q.box) {
								const [x1, y1, x2, y2] = q.box;
								mark = [X(x1), Y(y1), X(x2), Y(y2)];
								ctx.fillRect(
									mark[0],
									mark[1],
									mark[2] - mark[0],
									mark[3] - mark[1]
								);
								ctx.strokeRect(
									mark[0],
									mark[1],
									mark[2] - mark[0],
									mark[3] - mark[1]
								);
							} else if (q.point) {
								const [cx, cy, r] = [X(q.point[0]), Y(q.point[1]), 14 * dpr];
								mark = [cx - r, cy - r, cx + r, cy + r];
								ctx.beginPath();
								ctx.arc(cx, cy, r, 0, Math.PI * 2);
								ctx.fill();
								ctx.stroke();
							} else return;
							// a white label with a dark edge, readable on snow and on forest, beside the mark on whichever
							// side has room; none for a mark zoomed and panned out of view
							const label =
								q.label ||
								(asking ? 'add?' : following ? 'following…' : 'could not follow');
							const at = labelPlace({
								mark,
								y: (mark[1] + mark[3]) / 2 + 4 * dpr,
								width: ctx.measureText(label).width,
								W,
								H,
								gap: 6 * dpr,
								pad: 2 * dpr,
								ascent: 10 * dpr,
							});
							if (!at) return;
							const [lx, ly] = at;
							ctx.setLineDash([]);
							ctx.lineWidth = 3 * dpr;
							ctx.strokeStyle = 'rgba(0, 0, 0, 0.7)';
							ctx.strokeText(label, lx, ly);
							ctx.fillStyle =
								following || asking
									? 'rgba(245, 245, 245, 1)'
									: `rgba(${AMBER}, 1)`;
							ctx.fillText(label, lx, ly);
							ctx.lineWidth = 2 * dpr;
						});
						ctx.restore();
					}

					// pulse when an animal is first counted
					for (let i = lo; i < hi; i += 1) {
						const tr = tracks[trackOf[i]];
						const age = f - (tr.confirmed_at ?? tr.t0);
						if (age < 0 || age >= PULSE_FRAMES) continue;
						const k = age / PULSE_FRAMES;
						const j = pointOf[i];
						ctx.strokeStyle = `rgba(${LIGHT_GREEN}, ${(1 - k) * 0.8})`;
						ctx.lineWidth = 2 * dpr;
						ctx.beginPath();
						ctx.arc(X(tr.x[j]), Y(tr.y[j]), (6 + 16 * k) * dpr, 0, Math.PI * 2);
						ctx.stroke();
					}
				}

				// checking the count: the picture dimmed around the track asked about, its box ringed white; dimmed less
				// while it is not in the frame (the player loops from just before it starts to just after it ends)
				if (spotlight != null) {
					let box = null;
					for (let i = lo; i < hi; i += 1) {
						const tr = tracks[trackOf[i]];
						if (tr.id !== spotlight) continue;
						const j = pointOf[i];
						box = [
							X(tr.x[j] - tr.w[j] / 2),
							Y(tr.y[j] - tr.h[j] / 2),
							tr.w[j] * m.s,
							tr.h[j] * m.s,
						];
						break;
					}
					const pad = 14 * dpr;
					const radius = 10 * dpr;
					ctx.save();
					const shade = new Path2D();
					shade.rect(0, 0, W, H);
					if (box)
						shade.roundRect(
							box[0] - pad,
							box[1] - pad,
							box[2] + 2 * pad,
							box[3] + 2 * pad,
							radius
						);
					ctx.fillStyle = `rgba(0, 0, 0, ${box ? 0.55 : 0.3})`;
					ctx.fill(shade, 'evenodd');
					// a magnifier: the track's own pixels, live and larger, in the corner away from it, ringed alike
					const v = videoRef.current;
					if (box && v?.videoWidth && v.readyState >= 2) {
						const k = v.videoWidth / index.width;
						const cx = (box[0] + box[2] / 2 - m.ox) / m.s;
						const cy = (box[1] + box[3] / 2 - m.oy) / m.s;
						const side = Math.max(
							MAGNIFY_MIN_SRC / k,
							(MAGNIFY_CONTEXT * Math.max(box[2], box[3])) / m.s
						);
						const sx = Math.min(
							Math.max(0, (cx - side / 2) * k),
							v.videoWidth - side * k
						);
						const sy = Math.min(
							Math.max(0, (cy - side / 2) * k),
							v.videoHeight - side * k
						);
						const dw = Math.round(Math.min(W, H) * MAGNIFY_SHARE);
						const margin = 12 * dpr;
						const dx = box[0] + box[2] / 2 < W / 2 ? W - dw - margin : margin;
						const dy = box[1] + box[3] / 2 < H / 2 ? H - dw - margin : margin;
						const inset = new Path2D();
						inset.roundRect(dx, dy, dw, dw, radius);
						ctx.save();
						ctx.clip(inset);
						ctx.drawImage(v, sx, sy, side * k, side * k, dx, dy, dw, dw);
						// the track's box inside it, amber as on the question's pictures
						const z = dw / side;
						const bx = dx + ((box[0] - m.ox) / m.s - sx / k) * z;
						const by = dy + ((box[1] - m.oy) / m.s - sy / k) * z;
						ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)';
						ctx.lineWidth = 4 * dpr;
						ctx.strokeRect(bx, by, (box[2] / m.s) * z, (box[3] / m.s) * z);
						ctx.strokeStyle = `rgba(${AMBER}, 0.95)`;
						ctx.lineWidth = 2 * dpr;
						ctx.strokeRect(bx, by, (box[2] / m.s) * z, (box[3] / m.s) * z);
						ctx.restore();
						ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
						ctx.lineWidth = 2.5 * dpr;
						ctx.stroke(inset);
					}
					if (box) {
						const ring = new Path2D();
						ring.roundRect(
							box[0] - pad,
							box[1] - pad,
							box[2] + 2 * pad,
							box[3] + 2 * pad,
							radius
						);
						ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
						ctx.lineWidth = 5 * dpr;
						ctx.stroke(ring);
						ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
						ctx.lineWidth = 2.5 * dpr;
						ctx.stroke(ring);
					}
					ctx.restore();
				}

				// counting line
				if (line) {
					ctx.save();
					ctx.setLineDash([15 * dpr, 11 * dpr]);
					ctx.strokeStyle = 'rgba(245, 245, 245, 0.95)';
					ctx.lineWidth = 3 * dpr;
					ctx.beginPath();
					ctx.moveTo(X(line.start[0]), Y(line.start[1]));
					ctx.lineTo(X(line.end[0]), Y(line.end[1]));
					ctx.stroke();
					ctx.restore();
					if (lineEditable) {
						for (const p of [line.start, line.end]) {
							ctx.fillStyle = 'rgba(245, 245, 245, 0.95)';
							ctx.strokeStyle = `rgba(${GREEN}, 1)`;
							ctx.lineWidth = 2 * dpr;
							ctx.beginPath();
							ctx.arc(X(p[0]), Y(p[1]), 7 * dpr, 0, Math.PI * 2);
							ctx.fill();
							ctx.stroke();
						}
					}
					// +1 on the line where an animal is counted across it (−1 for one crossing back), when it counts: at the
					// crossing, or when the animal pops up if it crossed in the second before
					if (crossingMarks?.length) {
						const cross = Math.max(2, Math.round(CROSS_SECONDS * fps));
						ctx.save();
						ctx.font = `800 ${15 * dpr}px sans-serif`;
						ctx.lineJoin = 'round';
						for (const mk of crossingMarks) {
							const age = f - mk.frame;
							if (age < 0 || age >= cross) continue;
							const k = age / cross;
							const cx = X(mk.at[0]);
							const cy = Y(mk.at[1]);
							ctx.beginPath();
							ctx.arc(cx, cy, (6 + 12 * k) * dpr, 0, Math.PI * 2);
							ctx.strokeStyle = `rgba(0, 0, 0, ${0.45 * (1 - k)})`;
							ctx.lineWidth = 4.5 * dpr;
							ctx.stroke();
							ctx.strokeStyle = `rgba(${LIGHT_GREEN}, ${1 - k})`;
							ctx.lineWidth = 2.5 * dpr;
							ctx.stroke();
							const text = mk.plus ? '+1' : '−1';
							const tx = cx + 10 * dpr;
							const ty = cy - (10 + 10 * k) * dpr;
							ctx.lineWidth = 4 * dpr;
							ctx.strokeStyle = `rgba(0, 0, 0, ${0.75 * (1 - k)})`;
							ctx.strokeText(text, tx, ty);
							ctx.fillStyle = `rgba(${LIGHT_GREEN}, ${1 - k})`;
							ctx.fillText(text, tx, ty);
						}
						ctx.restore();
					}
				}
			},
			[
				index,
				line,
				lineEditable,
				crossingMarks,
				mapping,
				showDetections,
				showTrails,
				selectedIds,
				spotlight,
				proposals,
				pendingAdds,
				marker,
				kindColors,
				color,
			]
		);

		// repaint on every presented video frame
		useEffect(() => {
			const v = videoRef.current;
			if (!v || !index) return undefined;
			let handle = null;
			let raf = null;
			const report = seconds => {
				const f = frameAt(index, seconds);
				if (f !== frameRef.current) {
					frameRef.current = f;
					onFrame?.(f);
				}
				paint(f);
			};
			if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
				const cb = (_now, meta) => {
					report(meta.mediaTime);
					handle = v.requestVideoFrameCallback(cb);
				};
				handle = v.requestVideoFrameCallback(cb);
			} else {
				const loop = () => {
					report(v.currentTime);
					raf = requestAnimationFrame(loop);
				};
				raf = requestAnimationFrame(loop);
			}
			const onSeeked = () => report(v.currentTime);
			v.addEventListener('seeked', onSeeked);
			v.addEventListener('loadeddata', onSeeked);
			return () => {
				if (handle != null && v.cancelVideoFrameCallback)
					v.cancelVideoFrameCallback(handle);
				if (raf != null) cancelAnimationFrame(raf);
				v.removeEventListener('seeked', onSeeked);
				v.removeEventListener('loadeddata', onSeeked);
			};
		}, [index, paint, onFrame]);

		// the zoom goes onto the video (or the still) as a transform and into the next paint
		const applyView = useCallback(
			view => {
				viewRef.current = view;
				const t = viewTransform(view);
				if (videoRef.current) videoRef.current.style.transform = t;
				if (stillRef.current) stillRef.current.style.transform = t;
				paint(frameRef.current);
				// the label rounds to a tenth, but anything zoomed at all must read as zoomed (the chip, the drags),
				// so a soft trackpad step to 1.03 shows as 1.1
				const shown = view.k === 1 ? 1 : Math.max(1.1, Math.round(view.k * 10) / 10);
				setZoom(z => (z === shown ? z : shown));
			},
			[paint]
		);
		// the latest applyView for effects that must not re-run when paint changes (picking a dot changes paint)
		const applyViewRef = useRef(applyView);
		applyViewRef.current = applyView;

		// the wheel zooms around the cursor; its own listener because React's onWheel is passive and the page
		// must not scroll under a zoom. Only a turn-out on a plain picture scrolls as usual
		useEffect(() => {
			const el = wrapRef.current;
			if (!el) return undefined;
			const onWheel = e => {
				const rect = baseRect();
				if (!rect) return;
				const factor = wheelFactor(e);
				if (viewRef.current.k === 1 && factor < 1) return;
				e.preventDefault();
				// the cursor against the padding box, which is what contentRect measures (the corner view has a
				// border); an event without a position zooms around the middle of the picture
				const b = el.getBoundingClientRect();
				const cursor = Number.isFinite(e.clientX)
					? [
							e.clientX - b.left - (el.clientLeft || 0),
							e.clientY - b.top - (el.clientTop || 0),
						]
					: [rect.x + rect.w / 2, rect.y + rect.h / 2];
				const kMax = zoomMax(videoRef.current?.videoWidth || index?.width, rect.w);
				const next = zoomAt(viewRef.current, rect, cursor, factor, kMax);
				if (next !== viewRef.current) applyViewRef.current(next);
			};
			el.addEventListener('wheel', onWheel, { passive: false });
			return () => el.removeEventListener('wheel', onWheel);
		}, [index]);

		// a new video starts plain
		useEffect(() => {
			applyViewRef.current(PLAIN);
		}, [src]);

		// repaint when the layout or the line changes while paused; a frame that changed size (the window, full
		// screen) drops the zoom, since its offsets were for the old size
		useEffect(() => {
			paint(frameRef.current);
			const wrap = wrapRef.current;
			if (!wrap) return undefined;
			const ro = new ResizeObserver(() => {
				const size = `${wrap.clientWidth}x${wrap.clientHeight}`;
				const changed = sizeRef.current != null && sizeRef.current !== size;
				sizeRef.current = size;
				if (changed && viewRef.current.k !== 1) applyViewRef.current(PLAIN);
				else paint(frameRef.current);
			});
			ro.observe(wrap);
			return () => ro.disconnect();
		}, [paint]);

		// an added object being followed breathes, also while the video is paused
		const following = !!pendingAdds?.some(q => q.status === 'following');
		useEffect(() => {
			if (!following) return undefined;
			const id = setInterval(() => paint(frameRef.current), 120);
			return () => clearInterval(id);
		}, [following, paint]);

		// line editing: drag an end point, or the whole line
		const toSource = e => {
			const m = mapping();
			const rect = canvasRef.current.getBoundingClientRect();
			const cx = (e.clientX - rect.left) * m.dpr;
			const cy = (e.clientY - rect.top) * m.dpr;
			return [(cx - m.ox) / m.s, (cy - m.oy) / m.s];
		};
		const hit = p => {
			const m = mapping();
			const tol = (HANDLE_PX * m.dpr) / m.s;
			if (Math.hypot(p[0] - line.start[0], p[1] - line.start[1]) < tol) return 'start';
			if (Math.hypot(p[0] - line.end[0], p[1] - line.end[1]) < tol) return 'end';
			const [ax, ay] = line.start;
			const [bx, by] = line.end;
			const L2 = (bx - ax) ** 2 + (by - ay) ** 2 || 1;
			const u = Math.max(
				0,
				Math.min(1, ((p[0] - ax) * (bx - ax) + (p[1] - ay) * (by - ay)) / L2)
			);
			if (Math.hypot(p[0] - (ax + u * (bx - ax)), p[1] - (ay + u * (by - ay))) < tol)
				return 'line';
			return null;
		};
		const clampP = p => [
			Math.max(0, Math.min(index.width, Math.round(p[0]))),
			Math.max(0, Math.min(index.height, Math.round(p[1]))),
		];
		// a counting line's ends stay a band inside the frame's edges (an added box may reach the edge)
		const lineP = p => keepLineInside(p, index.width, index.height);
		// where a point in source pixels is on the picture, in CSS pixels (for a card next to it)
		const screenAt = p => {
			const m = mapping();
			return {
				x: m.r.x + p[0] * (m.s / m.dpr),
				y: m.r.y + p[1] * (m.s / m.dpr),
				width: wrapRef.current?.clientWidth || 0,
				height: wrapRef.current?.clientHeight || 0,
			};
		};
		// the track whose mark is under a click on the frame shown, if any (pickTrack.js): the dot in the dots view,
		// the box in the boxes view, so the animals beside a dotted one can be added
		const pickAt = p => {
			const m = mapping();
			if (!m || !index) return null;
			const hit = pickTrack(index, frameRef.current, p, {
				margin: (HANDLE_PX * m.dpr) / m.s,
				onDot: marker !== 'box',
			});
			if (!hit) return null;
			return { id: hit.id, frame: hit.frame, ...screenAt([hit.x, hit.y]) };
		};
		// zoomed in, a drag on the picture moves it; a press that does not move is the click it would have been
		// (`thenClick`), decided when the pointer comes up
		const startPan = (e, thenClick) => {
			panRef.current = {
				x: e.clientX,
				y: e.clientY,
				view: viewRef.current,
				moved: false,
				thenClick,
			};
			e.currentTarget.setPointerCapture(e.pointerId);
			setPanning(true);
		};
		const onPointerDown = e => {
			// no picture size yet (nothing analyzed to draw): a click only plays or pauses
			if (!mapping()) {
				if (tool) return;
				if (zoomed) startPan(e, toggle);
				else toggle();
				return;
			}
			if (onPick && showDetections) {
				const picked = pickAt(toSource(e));
				if (picked) {
					onPick(picked);
					return;
				}
			}
			if (tool === 'click' && onAddPoint) {
				const p = clampP(toSource(e));
				onAddPoint(frameRef.current, p, screenAt(p));
				return;
			}
			if (tool === 'box' && onAddBox) {
				const p0 = clampP(toSource(e));
				boxDragRef.current = { from: p0, to: p0 };
				e.currentTarget.setPointerCapture(e.pointerId);
				return;
			}
			if (tool) {
				if (zoomed) startPan(e, null);
				return;
			}
			if (!lineEditable || !line) {
				if (zoomed) startPan(e, toggle);
				else toggle();
				return;
			}
			const p = toSource(e);
			const part = hit(p);
			// zoomed in, a drag beside the line moves the picture, not a new line
			if (!part && zoomed) {
				startPan(e, null);
				return;
			}
			dragRef.current = part
				? { part, from: p, line }
				: { part: 'new', from: lineP(p), line };
			e.currentTarget.setPointerCapture(e.pointerId);
		};
		const onPointerMove = e => {
			const pan = panRef.current;
			if (pan) {
				const dx = e.clientX - pan.x;
				const dy = e.clientY - pan.y;
				if (!pan.moved && Math.hypot(dx, dy) < DRAG_PX) return;
				pan.moved = true;
				const rect = baseRect();
				if (rect) applyView(panBy(pan.view, rect, dx, dy));
				return;
			}
			if (!mapping()) return;
			if (boxDragRef.current) {
				boxDragRef.current.to = clampP(toSource(e));
				paint(frameRef.current);
				return;
			}
			if (tool || !lineEditable || !line) return;
			const p = toSource(e);
			const d = dragRef.current;
			if (!d) {
				const part = hit(p);
				// zoomed in, the open picture drags (no new line there)
				setCursor(part === 'line' ? 'move' : part || zoomed ? 'grab' : 'crosshair');
				return;
			}
			let next;
			if (d.part === 'start') next = { start: lineP(p), end: d.line.end };
			else if (d.part === 'end') next = { start: d.line.start, end: lineP(p) };
			else if (d.part === 'line') {
				const dx = p[0] - d.from[0];
				const dy = p[1] - d.from[1];
				next = {
					start: lineP([d.line.start[0] + dx, d.line.start[1] + dy]),
					end: lineP([d.line.end[0] + dx, d.line.end[1] + dy]),
				};
			} else next = { start: d.from, end: lineP(p) };
			onLineChange?.(next);
		};
		const onPointerUp = e => {
			dragRef.current = null;
			const pan = panRef.current;
			if (pan) {
				panRef.current = null;
				setPanning(false);
				// a press that did not move was a click, unless the browser canceled it
				if (!pan.moved && e?.type !== 'pointercancel') pan.thenClick?.();
				return;
			}
			const b = boxDragRef.current;
			if (b) {
				boxDragRef.current = null;
				const box = [
					Math.min(b.from[0], b.to[0]),
					Math.min(b.from[1], b.to[1]),
					Math.max(b.from[0], b.to[0]),
					Math.max(b.from[1], b.to[1]),
				];
				paint(frameRef.current);
				if (box[2] - box[0] >= 4 && box[3] - box[1] >= 4)
					onAddBox(frameRef.current, box, screenAt([box[2], box[1]]));
			}
		};

		const toggle = () => {
			const v = videoRef.current;
			if (!v) return;
			if (!v.paused) v.pause();
			// a source that failed or has no data yet rejects; the frame already says so, with Try again
			else if (canPlay) v.play()?.catch(() => {});
		};

		const showSwap = pip && onSwap;
		const swapButton = (
			<SwapButton
				size="small"
				shape="circle"
				icon={<LuMaximize2 />}
				onClick={onSwap}
				aria-label={swapped ? 'Show the video large' : 'Show the map large'}
				title={swapped ? 'Show the video large' : 'Show the map large'}
			/>
		);

		return (
			<ConfigProvider getPopupContainer={popupContainer}>
				<Player ref={rootRef}>
					<Wrap $ratio={aspect}>
						<Picture ref={wrapRef} $pip={!!pip && swapped} $ratio={aspect}>
							<Video
								ref={videoRef}
								src={stills ? undefined : src}
								crossOrigin={crossOrigin}
								preload="auto"
								autoPlay={autoPlay}
								playsInline
								muted
								onPlay={() => setPlaying(true)}
								onPause={() => setPlaying(false)}
								onTimeUpdate={e => setTime(e.currentTarget.currentTime)}
								onLoadedMetadata={e => {
									setDuration(e.currentTarget.duration);
									// a zoom made before the picture had a size was fitted to the whole frame
									if (viewRef.current.k !== 1) applyView(PLAIN);
								}}
								// loadeddata: the first frame is decoded, which iOS reports for a paused video where
								// it never reaches canplay before playback is asked for
								onLoadedData={settled}
								onCanPlay={settled}
								onPlaying={settled}
								onWaiting={() => setBuffering(true)}
								// a link that expired or a codec the browser lacks: no spinner claiming progress
								onError={() => {
									setBuffering(false);
									setFailed(true);
									setRetrying(false);
								}}
								onClick={toggle}
							/>
							{/* a still that mounts while zoomed takes the zoom at once, as the canvas already has it */}
							{still && (
								<Still
									ref={el => {
										stillRef.current = el;
										if (el) el.style.transform = viewTransform(viewRef.current);
									}}
									src={still.url}
									alt=""
								/>
							)}
							<Canvas
								ref={canvasRef}
								$interactive={lineEditable || !!onPick || !!tool || zoomed}
								$cursor={
									tool === 'click' || tool === 'box'
										? 'crosshair'
										: panning
											? 'grabbing'
											: zoomed && cursor === 'default'
												? 'grab'
												: cursor
								}
								onPointerDown={onPointerDown}
								onPointerMove={onPointerMove}
								onPointerUp={onPointerUp}
								onPointerCancel={onPointerUp}
							/>
							{/* after the canvas and before the corner views and overlays, which it must not cover */}
							{!stills && src && buffering && !failed && (
								<FrameCentre role="status" aria-label="Loading the video">
									<Spin size="large" />
								</FrameCentre>
							)}
							{!stills && src && failed && (
								<FrameCentre role="alert">
									<FailedNote>
										<span>The video could not be played.</span>
										<Button size="small" loading={retrying} onClick={reload}>
											Try again
										</Button>
									</FailedNote>
								</FrameCentre>
							)}
							{showSwap && swapped && swapButton}
							{corner && !swapped && <Corner>{corner}</Corner>}
						</Picture>
						{pip && (
							<PipSlot $pip={!swapped} $small={pipSmall}>
								{pip}
								{showSwap && !swapped && swapButton}
							</PipSlot>
						)}
						{children}
					</Wrap>
					<Controls ref={controlsRef}>
						{!stills && (
							<PlayButton
								type="button"
								onClick={toggle}
								disabled={!canPlay}
								aria-label={canPlay ? (playing ? 'Pause' : 'Play') : WAIT_TO_PLAY}
								aria-keyshortcuts={shortcuts && canPlay ? 'Space' : undefined}
								title={
									canPlay
										? shortcuts
											? `${playing ? 'Pause' : 'Play'} (Space)`
											: undefined
										: WAIT_TO_PLAY
								}>
								{playing ? <LuPause /> : <LuPlay />}
							</PlayButton>
						)}
						<ScrubWrap>
							<Scrub
								type="range"
								min={0}
								max={(stills ? stillsDuration : duration) || 0}
								step={stills ? 'any' : 1 / (index?.fps || 30)}
								value={stills ? stillTime : time}
								onChange={e => {
									if (stills) setStillTime(Number(e.target.value));
									else if (videoRef.current)
										videoRef.current.currentTime = Number(e.target.value);
								}}
								aria-label={stills ? 'Still frame' : 'Seek'}
							/>
							{!stills &&
								duration > 0 &&
								(editMarks || []).map((mk, i) => (
									<EditMark
										key={i}
										$kind={mk.kind}
										style={{
											left: `${Math.min(100, (100 * mk.sec) / duration)}%`,
										}}
									/>
								))}
						</ScrubWrap>
						<span>
							{fmt(stills ? (still?.t ?? 0) : time)} /{' '}
							{fmt(stills ? stillsDuration : duration)}
						</span>
						{!stills && (
							<Speed
								aria-label="Playback speed"
								defaultValue="1"
								onChange={e => {
									if (videoRef.current)
										videoRef.current.playbackRate = Number(e.target.value);
								}}>
								{[0.25, 0.5, 1, 2, 4].map(s => (
									<option key={s} value={s}>
										{s}×
									</option>
								))}
							</Speed>
						)}
						{/* always in the layout, hidden while plain: a bar that got taller with the chip would shrink the
						    picture (the page fits the picture to the height the bar leaves), and a picture that changed
						    size drops the zoom */}
						<ZoomChip
							size="small"
							style={{ visibility: zoomed ? 'visible' : 'hidden' }}
							aria-hidden={!zoomed}
							onClick={() => applyView(PLAIN)}
							aria-label={`${zoom.toFixed(1)}× · reset the zoom to the whole picture`}
							title="Back to the whole picture">
							{zoom.toFixed(1)}× · reset
						</ZoomChip>
						{canFullscreen && (
							<Button
								size="small"
								shape="circle"
								icon={fullscreen ? <LuMinimize /> : <LuMaximize />}
								onClick={toggleFullscreen}
								aria-label={fullscreen ? 'Leave full screen' : 'Full screen'}
								title={fullscreen ? 'Leave full screen (Esc)' : 'Full screen'}
							/>
						)}
						{shortcuts && !stills && (
							<Keys data-testid="player-keys">
								{/* Space does nothing while Play is off; the arrows still step */}
								{canPlay && (
									<>
										<kbd>Space</kbd> play
										<span aria-hidden="true">·</span>
									</>
								)}
								<kbd>←</kbd>
								<kbd>→</kbd> frame
								<span aria-hidden="true">·</span>
								<kbd>Shift</kbd>+<kbd>→</kbd> 1 s
							</Keys>
						)}
						{extraControls && <ChipRow>{extraControls}</ChipRow>}
					</Controls>
				</Player>
			</ConfigProvider>
		);
	}
);

OverlayPlayer.displayName = 'OverlayPlayer';

export default memo(OverlayPlayer);
